import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ShoppingBagIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const VendorOrders = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const queryClient = useQueryClient();

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const { data: ordersData, isLoading } = useQuery(
    ['vendor-orders', statusFilter],
    () => api.get('/orders/vendor/orders', {
      params: { status: statusFilter }
    }).then(res => res.data.data)
  );

  const updateStatusMutation = useMutation(
    ({ itemId, status }) => api.put(`/orders/items/${itemId}/status`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vendor-orders');
        toast.success('تم تحديث حالة الطلب');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  const updateAllItemsMutation = useMutation(
    ({ orderId, status }) => {
      // Update all items in the order
      const order = ordersData?.orders?.find(o => o.id === orderId);
      if (!order) return Promise.reject('Order not found');
      
      return Promise.all(
        order.items.map(item => 
          api.put(`/orders/items/${item.id}/status`, { status })
        )
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vendor-orders');
        toast.success('تم تحديث حالة جميع المنتجات في الطلب');
      },
      onError: () => toast.error('حدث خطأ')
    }
  );

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'معلق' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'قيد المعالجة' },
      shipped: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'مشحون' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', label: 'مسلم' },
      completed: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'مكتمل' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'ملغي' }
    };
    const s = statusMap[status] || statusMap.pending;
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${s.bg} ${s.text}`}>
        {s.label}
      </span>
    );
  };

  const shareOrderOnWhatsApp = (order) => {
    const customerName = order.customer?.name || order.shipping_address?.name || 'عميل مباشر';
    const phone = order.shipping_address?.phone || '';
    const address = order.shipping_address?.address || '';
    
    // بناء رسالة تفاصيل الطلب
    let message = `*تفاصيل الطلب #${order.order_number}*\n\n`;
    message += `📅 التاريخ: ${new Date(order.created_at).toLocaleDateString('ar-SA')}\n`;
    message += `👤 العميل: ${customerName}\n`;
    if (phone) message += `📱 الهاتف: ${phone}\n`;
    if (address) message += `📍 العنوان: ${address}\n`;
    message += `\n*المنتجات:*\n`;
    
    // إضافة المنتجات
    order.items?.forEach((item, index) => {
      message += `${index + 1}. ${item.product?.name || 'منتج'}\n`;
      message += `   الكمية: ${item.quantity} × ${item.price} جنيه = ${item.total} جنيه\n`;
    });
    
    message += `\n*الإجمالي: ${order.total_amount} جنيه*\n`;
    message += `الحالة: ${getStatusLabel(order.status)}`;
    
    // إنشاء رابط واتساب بدون رقم محدد - يفتح واتساب ويسمح باختيار المحادثة
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending: 'معلق',
      processing: 'قيد المعالجة',
      shipped: 'مشحون',
      delivered: 'مسلم',
      completed: 'مكتمل',
      cancelled: 'ملغي'
    };
    return statusMap[status] || 'معلق';
  };

  if (isLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">الطلبات</h1>

        {/* Filter */}
        <div className="bg-white p-4 rounded-lg shadow">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">جميع الطلبات</option>
            <option value="pending">معلق</option>
            <option value="processing">قيد المعالجة</option>
            <option value="shipped">مشحون</option>
            <option value="delivered">مسلم</option>
            <option value="completed">مكتمل</option>
            <option value="cancelled">ملغي</option>
          </select>
        </div>

        {/* Orders List */}
        {ordersData?.orders?.length > 0 ? (
          <div className="space-y-4">
            {ordersData.orders.map((order) => {
              const isExpanded = expandedOrders.has(order.id);
              
              return (
                <div key={order.id} className="bg-white rounded-lg shadow overflow-hidden">
                  {/* Order Header - Clickable */}
                  <div 
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleOrder(order.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-start space-x-3 space-x-reverse flex-1">
                        {/* Expand/Collapse Icon */}
                        <div className="mt-1">
                          {isExpanded ? (
                            <ChevronUpIcon className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        
                        {/* Order Info */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold">طلب #{order.order_number}</h3>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                            <span>{new Date(order.created_at).toLocaleDateString('ar-SA')}</span>
                            <span>•</span>
                            <span>العميل: {order.customer?.name || order.shipping_address?.name || 'عميل مباشر'}</span>
                            {order.shipping_address?.phone && (
                              <>
                                <span>•</span>
                                <span>{order.shipping_address.phone}</span>
                              </>
                            )}
                            <span>•</span>
                            <span className="font-medium">{order.items?.length || 0} منتج</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status, Amount and WhatsApp Button */}
                      <div className="flex items-center gap-3">
                        <div className="text-left space-y-2">
                          {getStatusBadge(order.status)}
                          <p className="text-lg font-bold text-primary-600">
                            {order.total_amount} جنيه
                          </p>
                        </div>
                        
                        {/* WhatsApp Share Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            shareOrderOnWhatsApp(order);
                          }}
                          className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                          title="مشاركة عبر واتساب"
                        >
                          <ChatBubbleLeftRightIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Order Details - Collapsible */}
                  {isExpanded && (
                    <div className="border-t bg-gray-50">
                      {/* Update Order Status */}
                      <div className="px-6 py-4 bg-white border-b">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">تحديث حالة الطلب:</span>
                          <select
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              if (e.target.value) {
                                updateAllItemsMutation.mutate({
                                  orderId: order.id,
                                  status: e.target.value
                                });
                                e.target.value = '';
                              }
                            }}
                            className="text-sm border border-primary-300 rounded px-3 py-2 bg-primary-50 focus:ring-2 focus:ring-primary-500"
                            defaultValue=""
                          >
                            <option value="" disabled>اختر الحالة الجديدة</option>
                            <option value="processing">قيد المعالجة</option>
                            <option value="shipped">مشحون</option>
                            <option value="delivered">مسلم</option>
                            <option value="completed">مكتمل</option>
                            <option value="cancelled">إلغاء</option>
                          </select>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="px-6 py-4">
                        <h4 className="font-medium mb-3 text-gray-900">المنتجات:</h4>
                        <div className="space-y-3">
                          {order.items?.map((item) => (
                            <div key={item.id} className="flex justify-between items-center py-3 px-4 bg-white rounded-lg border">
                              <div className="flex items-center flex-1">
                                {item.product?.images?.[0]?.url && (
                                  <img
                                    src={item.product.images[0].url}
                                    alt={item.product.name}
                                    className="w-16 h-16 rounded object-cover ml-3"
                                  />
                                )}
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{item.product?.name || 'منتج'}</p>
                                  <p className="text-sm text-gray-600">
                                    الكمية: {item.quantity} × {item.price} جنيه = <span className="font-medium">{item.total} جنيه</span>
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 space-x-reverse">
                                {getStatusBadge(item.status)}
                                {item.status === 'pending' && (
                                  <select
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => updateStatusMutation.mutate({
                                      itemId: item.id,
                                      status: e.target.value
                                    })}
                                    className="text-sm border border-gray-300 rounded px-2 py-1"
                                    defaultValue=""
                                  >
                                    <option value="" disabled>تحديث</option>
                                    <option value="processing">قيد المعالجة</option>
                                    <option value="shipped">مشحون</option>
                                    <option value="delivered">مسلم</option>
                                    <option value="completed">مكتمل</option>
                                  </select>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Shipping Address */}
                      {order.shipping_address && (
                        <div className="px-6 py-4 border-t bg-white">
                          <h4 className="font-medium mb-2 text-gray-900">عنوان الشحن:</h4>
                          <div className="text-sm text-gray-600 space-y-1">
                            {typeof order.shipping_address === 'string' ? (
                              <p>{order.shipping_address}</p>
                            ) : (
                              <>
                                {order.shipping_address?.name && (
                                  <p><span className="font-medium">الاسم:</span> {order.shipping_address.name}</p>
                                )}
                                {order.shipping_address?.phone && (
                                  <p><span className="font-medium">الهاتف:</span> {order.shipping_address.phone}</p>
                                )}
                                {order.shipping_address?.address && (
                                  <p><span className="font-medium">العنوان:</span> {order.shipping_address.address}</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <ShoppingBagIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">لا توجد طلبات</p>
          </div>
        )}
      </div>
    </VendorLayout>
  );
};

export default VendorOrders;